import React, { useEffect } from 'react';

const MemberServices = () => {
  useEffect(() => {
    document.title = 'Member Services - KPCA Professional Cleaning Services';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover the benefits of working with KPCA certified cleaning professionals. Quality assurance, professional standards, and reliable service delivery.');
    }
  }, []);

  const clientBenefits = [
    {
      icon: '✅',
      title: 'Certified Professionals',
      description: 'All KPCA members are trained and certified professionals who meet strict quality standards'
    },
    {
      icon: '🛡️',
      title: 'Quality Assurance',
      description: 'Members follow KPCA quality guidelines and best practices ensuring consistent, high-quality service'
    },
    {
      icon: '📋',
      title: 'Insurance & Compliance',
      description: 'KPCA members maintain proper insurance coverage and comply with all regulatory requirements'
    },
    {
      icon: '🤝',
      title: 'Professional Standards',
      description: 'Members adhere to our code of conduct ensuring ethical business practices and professionalism'
    },
    {
      icon: '⚡',
      title: 'Reliable Service',
      description: 'Verified track record of dependable service delivery and customer satisfaction'
    },
    {
      icon: '📞',
      title: 'Support & Resolution',
      description: 'KPCA provides mediation and support for any service-related issues or disputes'
    }
  ];

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-gray-900 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              🎯 Member Services
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Discover the benefits of working with KPCA certified cleaning professionals
            </p>
          </div>
        </div>
      </section>

      {/* Client Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              🎯 Why Choose KPCA Certified Members?
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              When you choose a KPCA certified cleaning professional, you're getting more than just cleaning services – 
              you're getting peace of mind, quality assurance, and professional excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clientBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              🏢 Service Categories
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our certified members offer a comprehensive range of professional cleaning services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Commercial Cleaning</h3>
              <p className="text-gray-600 leading-relaxed">Office buildings, retail spaces, and commercial facilities</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-4xl mb-4">🏠</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Residential Cleaning</h3>
              <p className="text-gray-600 leading-relaxed">Homes, apartments, and residential complexes</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-4xl mb-4">🏭</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Industrial Cleaning</h3>
              <p className="text-gray-600 leading-relaxed">Manufacturing facilities, warehouses, and industrial sites</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-4xl mb-4">🏥</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Healthcare Cleaning</h3>
              <p className="text-gray-600 leading-relaxed">Hospitals, clinics, and medical facilities</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-4xl mb-4">🧽</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Specialized Cleaning</h3>
              <p className="text-gray-600 leading-relaxed">Carpet cleaning, window cleaning, and deep cleaning services</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="text-4xl mb-4">🆘</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Emergency Services</h3>
              <p className="text-gray-600 leading-relaxed">Flood cleanup, fire damage restoration, and emergency response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Database Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              📊 KPCA Member Network
            </h2>
            <div className="bg-blue-50 rounded-lg p-8 mb-8">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">500+</div>
                  <p className="text-blue-800">Registered Members</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">450+</div>
                  <p className="text-green-800">Active Paid-up Members</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">47</div>
                  <p className="text-orange-800">Counties Covered</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Our comprehensive network includes only current, paid-up members who have met all certification requirements 
                and are in good standing with KPCA. All member information is verified and regularly updated to ensure accuracy.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-400 p-6 text-left">
              <h3 className="text-lg font-bold text-green-800 mb-2">
                ✅ Quality Guarantee
              </h3>
              <p className="text-green-700">
                All KPCA members are bound by our code of conduct and quality standards. We provide ongoing support 
                and mediation to ensure client satisfaction and professional service delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Services */}
      <section className="py-12 bg-primary-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold mb-4">Ready to Work with KPCA Members?</h3>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            Find certified cleaning professionals in your area and experience the difference of working with KPCA members.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/verify-member"
              className="bg-white text-primary-600 hover:bg-gray-100 py-3 px-6 rounded-lg font-medium transition-colors duration-200"
            >
              🔍 Verify Member
            </a>
            <a
              href="/contact"
              className="bg-primary-700 hover:bg-primary-800 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
            >
              📞 Contact Us
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MemberServices; 